<template>
  <v-card class="h-100 rounded-lg" elevation="0" data-testid="top-delta-widget">
    <div v-if="widgetData.loading" class="px-6" data-testid="top-delta-widget-loading">
      <v-skeleton-loader type="heading" height="100" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
    </div>

    <template v-else-if="widgetData.data">
      <v-card-title class="d-flex justify-space-between align-center px-6 pt-6">
        <span class="subtitle2" data-testid="top-delta-widget-title">
          {{ $t(`mspDashboard.widgets.${id}.title`) }}
        </span>
        <v-select
          v-model="selectedRange"
          :items="presets"
          density="compact"
          variant="outlined"
          hide-details
          rounded
          style="max-width: max-content"
          class="rounded-lg"
          item-title="label"
          item-value="value"
          return-object
          data-testid="top-delta-widget-range-select"
          @click="sendGtmEvent('Time selector clicked')"
        ></v-select>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="chart-container">
          <!-- No Data State -->
          <div
            v-if="!chartItems.length"
            class="d-flex flex-column align-center justify-center h-100 pb-6 empty-state-wrapper"
            data-testid="top-delta-widget-no-data"
          >
            <v-icon
              class="mb-1"
              icon="$dataNoHexagon"
              size="80"
              data-testid="top-delta-widget-no-data-icon"
            ></v-icon>
            <div class="headline6 mb-1">
              {{ $t(`mspDashboard.widgets.${id}.noDataTitle`) }}
            </div>
            <div class="body2 text-center text-indigo-medium">
              {{ $t(`mspDashboard.widgets.${id}.noDataDescription`) }}
            </div>
          </div>
          <div
            v-else
            v-for="item in chartItems"
            :key="item.workspaceId"
            class="chart-row"
            :data-testid="`top-delta-widget-item-${item.workspaceId}`"
          >
            <ellipsified-copy-text class="caption text-indigo-medium" :text="item.workspaceName" />
            <div class="d-flex align-center">
              <div class="bar-negative">
                <template v-if="item.delta < 0">
                  <div class="caption text-right" data-testid="top-delta-widget-negative-value">
                    {{ item.delta }}
                  </div>
                  <div class="bar bg-red-base" :style="{ width: item.width + '%' }"></div>
                </template>
              </div>
              <div class="bar-positive">
                <template v-if="item.delta >= 0">
                  <div class="bar bg-green-base" :style="{ width: item.width + '%' }"></div>
                  <div class="caption text-left" data-testid="top-delta-widget-positive-value">
                    {{ item.delta }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </template>

    <div
      v-else
      class="h-100 d-flex align-center justify-center flex-column px-6"
      data-testid="widget-error-state"
    >
      <v-icon icon="$alertTriangle" size="80"></v-icon>
      <span class="subtitle1 mb-4 text-center"> {{ $t(`mspDashboard.widgets.${id}.error`) }}</span>
      <div
        class="coro-link"
        @click="$emit('retry-fetch-data', selectedRange.value)"
        data-testid="top-delta-widget-retry"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, type PropType } from "vue";
import type { WorkspaceDeltaItem } from "@/_store/msp/msp-dashboard.module.ts";
import type { DataValue } from "@/types.ts";
import { i18n } from "@/plugins/i18n.ts";
import { getNDaysAgo, type PresetDate } from "@/constants/date-range-picker.ts";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
/**
 * Widget ID type
 */
type TopDeltaWidgetId = "topUsersDeltaWidget" | "topDevicesDeltaWidget";

/**
 * Chart item type with calculated width
 */
interface ChartItem extends WorkspaceDeltaItem {
  width: number;
}

export default defineComponent({
  components: { EllipsifiedCopyText },
  props: {
    id: {
      type: String as PropType<TopDeltaWidgetId>,
      required: true,
    },
    widgetData: {
      type: Object as PropType<DataValue<WorkspaceDeltaItem[]>>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props, { emit }) {
    /**
     * Calculate date range presets
     */
    const presets = computed<PresetDate[]>(() => {
      return [
        {
          label: i18n.global.t("dateRangePicker.lastSevenDays"),
          value: getNDaysAgo(7),
        },
        {
          label: i18n.global.t("dateRangePicker.lastThirtyDays"),
          value: getNDaysAgo(30),
        },
        {
          label: i18n.global.t("dateRangePicker.lastNinetyDays"),
          value: getNDaysAgo(90),
        },
      ];
    });

    const selectedRange = ref<PresetDate>(presets.value[0]);

    /**
     * Calculate maximum values for positive and negative deltas
     */
    const getMaxValues = (
      data: WorkspaceDeltaItem[]
    ): { maxPositive: number; maxNegative: number } => {
      const positiveValues = data.filter((d) => d.delta >= 0).map((d) => d.delta);
      const negativeValues = data.filter((d) => d.delta < 0).map((d) => Math.abs(d.delta));

      return {
        maxPositive: Math.max(...positiveValues, 1),
        maxNegative: Math.max(...negativeValues, 1),
      };
    };

    /**
     * Calculate width percentage for chart bars
     */
    const calculateWidth = (delta: number, maxPositive: number, maxNegative: number): number => {
      if (delta >= 0) {
        return (delta / maxPositive) * 100;
      } else {
        return (Math.abs(delta) / maxNegative) * 100;
      }
    };

    /**
     * Prepare chart items with calculated widths
     */
    const chartItems = computed<ChartItem[]>(() => {
      const data = props.widgetData.data ?? [];
      if (data.length === 0) return [];

      const { maxPositive, maxNegative } = getMaxValues(data);

      return data
        .filter((v) => v.delta !== 0)
        .map((item) => ({
          ...item,
          width: calculateWidth(item.delta, maxPositive, maxNegative),
        }));
    });

    const sendGtmEvent = (event: string) => {
      trackGtmEvent(GtmAction.MSP_DASHBOARD_WIDGET, `${startCase(props.id)} - ${event}`);
    };

    // Watch for changes in selected range and emit event to fetch new data
    watch(selectedRange, (newRange) => {
      emit("retry-fetch-data", newRange.value);
    });

    return {
      selectedRange,
      presets,
      chartItems,
      sendGtmEvent,
    };
  },
});
</script>

<style lang="scss" scoped>
.chart-container {
  display: grid;
  grid-template-columns: fit-content(30%) 1fr;
  gap: 12px;
  align-items: center;
}

.chart-row {
  /*
    - 'grid-column: 1 / -1' makes it span the full width of the parent grid.
  */
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.chart-row > div:first-child {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-negative {
  flex: 1 1 50%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}
.bar-positive {
  flex: 1 1 50%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
}
.bar {
  height: 8px;
  border-radius: 9999px;
}

/* When there is no data, we need to ensure the container spans both columns */
.empty-state-wrapper {
  grid-column: 1 / -1;
}

:deep(.empty-state-wrapper) {
  .icon-data-no-hex-l:before {
    color: rgb(var(--v-theme-indigo-light)) !important;
  }
}
</style>
